import type { MessageSchema } from './en'

const fr: MessageSchema = {
  common: {
    cancel: 'Annuler',
    create: 'Créer',
    save: 'Enregistrer',
    saving: 'Enregistrement…',
    refresh: 'Actualiser',
    loading: 'Chargement…'
  },
  nav: {
    products: 'Produits',
    upload: 'Importer',
    adhoc: 'Ad hoc',
    reports: 'Rapports',
    settings: 'Paramètres'
  },
  sidebar: {
    expand: 'Développer',
    collapse: 'Réduire',
    expandSidebar: 'Développer la barre latérale',
    collapseSidebar: 'Réduire la barre latérale',
    logout: 'Déconnexion'
  },
  topbar: {
    welcome: 'Bienvenue, {name}',
    selectLanguage: 'Sélectionner la langue'
  },
  footer: {
    copyright: 'Copyright © {year} Avalara Inc. Tous droits réservés.'
  },
  auth: {
    redirecting: 'Redirection vers la connexion…',
    completing: 'Finalisation de l’authentification…',
    noCode: 'Aucun code d’autorisation reçu',
    failed: 'Échec de l’authentification. Veuillez réessayer.',
    tryAgain: 'Réessayer'
  },
  settings: {
    profiles: 'Profils',
    users: 'Utilisateurs'
  },
  reporting: {
    userTitle: 'Rapports utilisateur',
    userSubtitle: 'Consultez et téléchargez les rapports liés à votre activité',
    userEmpty: 'Aucun rapport n’est disponible pour le moment.',
    profileTitle: 'Rapports de profil',
    profileSubtitle: 'Consultez et téléchargez les rapports liés à l’activité de votre profil',
    profileEmpty: 'Aucun rapport de profil n’est disponible pour le moment.',
    loading: 'Chargement des rapports…',
    emptyTitle: 'Aucun rapport disponible',
    download: 'Télécharger',
    loadFailed: 'Échec du chargement des rapports'
  },
  eccn: {
    title: 'Classification ECCN en libre-service',
    subtitle: 'Saisissez la description de votre produit pour obtenir une classification ECCN automatisée',
    productLabel: 'Description du produit',
    productPlaceholder: 'Saisissez une description détaillée du produit, ses spécifications et son usage prévu…',
    descriptionRequired: 'Veuillez saisir une description de produit',
    clear: 'Effacer',
    classify: 'Classer le produit',
    classifying: 'Classification en cours…',
    resultTitle: 'Résultat de la classification',
    confidence: '{value} % de confiance',
    code: 'ECCN',
    description: 'Description',
    controlReason: 'Motif de contrôle',
    exportRestrictions: 'Restrictions à l’exportation',
    noRestrictions: 'Aucune restriction particulière',
    downloadReport: 'Télécharger le rapport',
    saveClassification: 'Enregistrer la classification',
    readyTitle: 'Prêt à classer',
    readyHint: 'Saisissez une description de produit puis cliquez sur « {action} » pour commencer'
  },
  dashboard: {
    totalProducts: 'Total des produits',
    totalProductsChange: '+12 % par rapport au mois dernier',
    classifications: 'Classifications',
    classificationsChange: 'Produits vérifiés aujourd’hui',
    activeUsers: 'Utilisateurs actifs',
    activeUsersChange: 'Actuellement en ligne',
    complianceRate: 'Taux de conformité',
    complianceRateChange: '+2,1 % d’amélioration',
    quickUpload: 'Import rapide',
    quickUploadHint: 'Importez et classez rapidement de nouveaux produits',
    dropFiles: 'Glissez-déposez vos fichiers ici',
    browseFiles: 'Parcourir les fichiers',
    recentActivity: 'Activité récente',
    recentActivityHint: 'Dernières activités de classification',
    classifiedSuccessfully: 'Classé avec succès',
    pendingReview: 'En attente de révision',
    hoursAgo: 'il y a {count} h',
    daysAgo: 'il y a {count} j',
    systemStatus: 'État du système',
    systemStatusHint: 'État actuel des services de conformité commerciale',
    serviceClassificationApi: 'API de classification',
    serviceFileUpload: 'Import de fichiers',
    serviceReporting: 'Rapports',
    operational: 'Opérationnel',
    issues: 'Incidents'
  },
  profiles: {
    addChild: 'Ajouter un profil enfant',
    idLabel: 'ID : {id}',
    information: 'Informations sur le profil',
    nameLabel: 'Nom du profil',
    namePlaceholder: 'Saisissez le nom du profil',
    parentIdLabel: 'ID parent',
    noParent: 'Aucun (racine)',
    save: 'Enregistrer le profil',
    emptyTitle: 'Sélectionnez un profil',
    emptyHint: 'Sélectionnez un profil dans l’arborescence pour afficher et modifier ses détails',
    createTitle: 'Créer un profil enfant',
    loadFailed: 'Échec du chargement des profils',
    detailsFailed: 'Échec du chargement des détails du profil',
    saved: 'Profil enregistré avec succès',
    saveFailed: 'Échec de l’enregistrement du profil',
    nameRequired: 'Veuillez indiquer un nom de profil',
    created: 'Profil créé avec succès',
    duplicateName: 'Un profil portant ce nom existe déjà',
    createFailed: 'Échec de la création du profil'
  },
  users: {
    count: '{count} utilisateur | {count} utilisateurs',
    addNew: 'Ajouter un utilisateur',
    edit: 'Modifier l’utilisateur',
    username: 'Nom d’utilisateur',
    email: 'E-mail',
    firstName: 'Prénom',
    lastName: 'Nom',
    role: 'Rôle',
    roleUser: 'Utilisateur',
    roleAdmin: 'Administrateur',
    status: 'Statut',
    active: 'Actif',
    inactive: 'Inactif',
    lastLogin: 'Dernière connexion',
    lastLogout: 'Dernière déconnexion',
    assignedProfiles: 'Profils attribués',
    loadingProfiles: 'Chargement des profils…',
    resetPassword: 'Réinitialiser le mot de passe',
    loadFailed: 'Échec du chargement des utilisateurs',
    requiredFields: 'Le nom d’utilisateur, le prénom et l’e-mail sont obligatoires',
    profileRequired: 'Veuillez sélectionner au moins un profil',
    usernameExists: 'Ce nom d’utilisateur existe déjà',
    saved: 'Utilisateur enregistré avec succès',
    saveFailed: 'Échec de l’enregistrement de l’utilisateur',
    passwordReset: 'Mot de passe réinitialisé avec succès',
    passwordResetFailed: 'Échec de la réinitialisation du mot de passe'
  },
  upload: {
    colUploaded: 'Importé le',
    colClient: 'Client',
    colProfile: 'Profil',
    colDescription: 'Description',
    colFileName: 'Nom du fichier',
    colQuantity: 'Quantité',
    colStatus: 'Statut',
    colErrors: 'Erreurs',
    stateNew: 'Nouveau',
    statePending: 'En attente',
    stateRunning: 'En cours',
    stateProcessing: 'Traitement',
    stateCompleted: 'Terminé',
    stateError: 'Erreur',
    countProcessed: 'Traités : {processed} sur {total}',
    countSuccess: 'Réussis : {count}',
    countErrors: 'Erreurs : {count}',
    countWarnings: 'Avertissements : {count}',
    configTitle: 'Configuration de l’import',
    configSubtitle: 'Configurez les paramètres de votre import',
    clientLabel: 'Client',
    clientPlaceholder: 'Nom du client',
    profileLabel: 'Profil',
    profilePlaceholder: 'Sélectionnez un profil…',
    descriptionLabel: 'Description',
    descriptionPlaceholder: 'Saisissez une description pour cet import…',
    destinationsLabel: 'Destinations',
    destinationsPlaceholder: 'Sélectionnez des destinations…',
    duplicateStrategy: 'Gestion des doublons',
    overwriteDuplicates: 'Écraser les doublons',
    ignoreDuplicates: 'Ignorer les doublons',
    actionsLabel: 'Actions',
    classification: 'Classification',
    fileTitle: 'Import de fichier',
    fileSubtitle: 'Glissez-déposez ou sélectionnez les fichiers à importer',
    dropTitle: 'Glissez-déposez vos fichiers ici',
    formatHint: 'Le fichier doit être au format Excel (.xlsx). Pour télécharger un modèle,',
    clickHere: 'cliquez ici',
    headerRowHint: 'La première ligne du fichier est réservée aux en-têtes',
    selectFile: 'Sélectionner un fichier à importer',
    selectAll: 'Tout sélectionner',
    deselectAll: 'Tout désélectionner',
    uploadedFiles: 'Fichiers importés',
    autoRefresh: 'Actualisation automatique',
    confirmTitle: 'Confirmer l’import',
    confirmBody: 'Ce fichier sera importé vers {profile}. Confirmez-vous ?',
    uploadAction: 'Importer',
    listFailed: 'Échec du chargement de la liste des fichiers',
    xlsxOnly: 'Seuls les fichiers .xlsx sont autorisés',
    tooLarge: 'La taille du fichier doit être inférieure à 10 Mo',
    processingStarted: 'Enregistrement du fichier et démarrage du traitement…',
    saved: 'Fichier enregistré, il sera traité sous peu',
    uploadFailed: 'Échec de l’import du fichier',
    templateFailed: 'Échec du téléchargement du modèle'
  },
  reports: {
    userLabel: 'Utilisateur',
    profileLabel: 'Profil',
    from: 'Du',
    to: 'Au',
    exporting: 'Exportation…',
    exportDetails: 'Exporter les détails',
    productStatistics: 'Statistiques des produits',
    addedProducts: 'Produits ajoutés :',
    attemptedClassifications: 'Classifications tentées :',
    classified: 'Classés :',
    unclassified: 'Non classés :',
    classifiedLabel: 'Classés',
    noData: 'Aucune donnée disponible',
    noDataShort: 'Aucune donnée',
    classificationsOverTime: 'Classifications dans le temps',
    totalAttempted: 'Total des classifications tentées :',
    classificationsTooltip: '{count} classifications',
    topItems: 'Top 5 des articles',
    topItemsHint: 'Les 5 principaux articles pour tous les produits',
    topCategories: 'Top 5 des catégories',
    topCategoriesHint: 'Les 5 principales catégories pour les produits non classés',
    topCodes: 'Top 5 des codes',
    topCodesHint: 'Les 5 principaux codes pour les produits classés',
    exported: 'Rapport exporté avec succès',
    exportFailed: 'Échec de l’exportation du rapport'
  },
  adhoc: {
    title: 'Classification de produit',
    subtitle: 'Saisissez les détails du produit ci-dessous pour obtenir instantanément un code SH.',
    descriptionLabel: 'Description',
    descriptionPlaceholder: 'Décrivez le produit ici…',
    descriptionHelp: 'Fournissez une description détaillée du produit, y compris les matériaux, l’usage et les caractéristiques principales.',
    originLabel: 'Origine',
    originPlaceholder: 'Sélectionnez une origine…',
    destinationLabel: 'Destination',
    destinationPlaceholder: 'Sélectionnez une destination…',
    classify: 'Classer',
    classifying: 'Classification en cours…',
    reset: 'Réinitialiser',
    tipsTitle: 'Conseils de classification',
    tipsBestResults: 'Pour de meilleurs résultats :',
    tipMaterial: 'Indiquez la composition des matériaux',
    tipUse: 'Précisez l’usage ou la fonction prévus',
    tipCharacteristics: 'Mentionnez les principales caractéristiques physiques',
    tipBrand: 'Indiquez la marque ou le modèle le cas échéant',
    tipsExamples: 'Exemples courants :',
    exampleShirt: 'T-shirt en coton pour homme, taille L',
    exampleKnife: 'Couteau de cuisine en acier inoxydable, 8 pouces',
    exampleBattery: 'Batterie lithium-ion pour smartphones',
    exampleTable: 'Table de salle à manger en chêne, 6 places',
    fieldDescription: 'description du produit',
    fieldOrigin: 'origine',
    fieldDestination: 'destination',
    missingFields: 'Veuillez renseigner tous les champs obligatoires : {fields}',
    classificationTitle: 'Classification',
    classificationTitleFor: 'Classification : {description}',
    completed: 'Classification terminée',
    completedCreateRule: 'Classification terminée. Créez votre règle maintenant.',
    classifierNotStarted: 'La fenêtre de classification ne s’est pas chargée. Cliquez pour réessayer.',
    missingHsCode: 'Code SH manquant pour la cotation',
    quoteTitle: 'Estimation des droits et taxes',
    quoteConfirmHeading: 'Souhaitez-vous calculer les droits et taxes ?',
    quoteConfirmBody: 'Cela vous permettra d’estimer les droits et taxes pour le produit classé.',
    classificationSummary: 'Résumé de la classification',
    hsCodeLabel: 'Code SH :',
    originValueLabel: 'Origine :',
    destinationValueLabel: 'Destination :',
    stackedHsCodes: 'Codes SH cumulés :',
    noCancel: 'Non, annuler',
    yesContinue: 'Oui, continuer',
    quoteParamsTitle: 'Saisir les paramètres de cotation',
    classificationDetails: 'Détails de la classification',
    optionalParameters: 'Paramètres facultatifs',
    shipmentType: 'Type d’expédition',
    adValorem: 'Ad valorem',
    specific: 'Spécifique',
    currency: 'Devise',
    countryOfOrigin: 'Pays d’origine',
    selectCoo: 'Sélectionnez un pays d’origine…',
    quantity: 'Quantité',
    unitValue: 'Valeur unitaire ($)',
    total: 'Total ($)',
    shipping: 'Expédition ($)',
    handling: 'Manutention ($)',
    insurance: 'Assurance ($)',
    measureValue: 'Valeur de mesure',
    unit: 'Unité',
    calculating: 'Calcul en cours…',
    getQuote: 'Obtenir une estimation',
    quoteResultsTitle: 'Résultats de l’estimation des droits et taxes',
    quoteSummary: 'Résumé de l’estimation',
    quoteIdLabel: 'ID de l’estimation :',
    typeLabel: 'Type :',
    shipmentLabel: 'Expédition :',
    currencyLabel: 'Devise :',
    lineHeading: 'Ligne {number} – {description}',
    hsBadge: 'SH : {code}',
    duties: 'Droits',
    taxes: 'Taxes',
    colDescription: 'Description',
    colAmount: 'Montant',
    colType: 'Type',
    colTarget: 'Cible',
    colRate: 'Taux',
    dutyDetails: 'Détail des droits et application du code SH',
    rateLabel: 'Taux :',
    methodLabel: 'Méthode :',
    labelLabel: 'Libellé :',
    totalDuty: 'Total des droits',
    totalTax: 'Total des taxes',
    grandTotal: 'Total général',
    disclaimerLabel: 'Avertissement :',
    disclaimerBody: 'Ces estimations sont fournies à titre indicatif uniquement et peuvent ne pas refléter les droits et taxes réellement appliqués à l’importation.',
    close: 'Fermer'
  },
  products: {
    tabDetails: 'Détails',
    tabDestinations: 'Destinations',
    tabHistory: 'Historique',
    tabAudit: 'Audit',
    tabCopy: 'Copier',
    colDate: 'Date',
    colId: 'ID',
    colProfile: 'Profil',
    colDescription: 'Description',
    colCustomsDesc: 'Desc. douanière',
    colItem: 'Article',
    colCategory: 'Catégorie',
    colDutyRates: 'Taux de droits',
    productId: 'ID du produit',
    description: 'Description',
    customsDescription: 'Description douanière',
    client: 'Client',
    source: 'Source',
    origin: 'Origine',
    selectOrigin: 'Sélectionnez une origine…',
    manufacturerName: 'Nom du fabricant',
    reclassify: 'Reclasser',
    classify: 'Classer',
    reset: 'Réinitialiser',
    save: 'Enregistrer',
    selectProductDetails: 'Sélectionnez un produit pour afficher ses détails',
    tariffPackageRequired: 'Pour accéder à cette fonctionnalité, vous avez besoin du module de données tarifaires.',
    destination: 'Destination',
    selectDestination: 'Sélectionnez une destination…',
    hsCode: 'Code SH',
    lookupTariffCode: 'Rechercher le code tarifaire',
    tariffCode: 'Code tarifaire',
    addTariffCode: 'Ajouter le code tarifaire',
    viewCompliance: 'Afficher les détails de conformité',
    removeDestination: 'Supprimer la destination',
    valid: 'Valide',
    invalid: 'Non valide',
    restrictions: 'Restrictions',
    selectProductDestinations: 'Sélectionnez un produit pour afficher ses destinations',
    noHistory: 'Aucun historique disponible',
    questions: 'Questions',
    rationale: 'Justification',
    hsVersion: 'Version SH',
    noAudit: 'Aucune information d’audit disponible',
    copyFields: 'Copier les champs',
    copyTo: 'Copier vers',
    checkedProducts: 'Les produits que j’ai cochés',
    allListedProducts: 'Tous les produits actuellement affichés',
    productCount: '{count} produits',
    whenDataExists: 'Lorsque des données existent déjà',
    overwrite: 'Écraser les données existantes',
    doNotOverwrite: 'Ne pas écraser les données existantes',
    copying: 'Copie en cours…',
    copy: 'Copier',
    selectProductCopy: 'Sélectionnez un produit pour configurer les options de copie',
    loadingProduct: 'Chargement des informations du produit…',
    matching: 'Correspondants : {count}',
    checked: 'Cochés : {count}',
    exportAction: 'Exporter',
    reclassifyAction: 'Reclasser',
    addProduct: 'Ajouter un produit',
    filters: 'Filtres',
    newProduct: 'Nouveau produit',
    productIdRequired: 'L’ID du produit est obligatoire.',
    descriptionRequired: 'La description est obligatoire.',
    profile: 'Profil',
    selectProfile: 'Sélectionnez un profil…',
    reclassifyTitle: 'Reclasser les produits',
    reclassifyConfirm: 'Voulez-vous vraiment reclasser {count} produits ?',
    reclassifyNeedsSelection: 'Cochez d’abord les produits à reclasser',
    reclassifyScopeQuestion: 'Quels produits voulez-vous reclasser ?',
    reclassifyScopeChecked: 'Seulement les produits que j’ai cochés',
    reclassifyScopeAll: 'Tous les produits du filtre, sur toutes les pages',
    reclassifyWarning: 'Cela peut affecter les autres utilisateurs jusqu’à la fin du traitement.',
    reclassifyProgress: 'Reclassement des produits… {count}',
    processing: 'Traitement en cours…',
    complianceTitle: 'Informations de conformité',
    add: 'ADD',
    cvd: 'CVD',
    pga: 'PGA',
    yes: 'OUI',
    no: 'NON',
    mfnRate: 'Taux NPF',
    exportTitle: 'Exporter les produits',
    exportConfirm: 'Voulez-vous exporter la liste actuelle des produits (selon les filtres actifs) vers Excel ?',
    exportHint: 'Le fichier sera généré et votre navigateur lancera le téléchargement automatiquement.',
    preparingExcel: 'Préparation du fichier Excel. Votre navigateur lancera le téléchargement sous peu.',
    dutyRatesTitle: 'Taux de droits',
    loadingDutyRates: 'Chargement des taux de droits…',
    dutyRateDetails: 'Détail des taux de droits',
    noDetailedDutyRates: 'Aucun taux de droits détaillé disponible.',
    noDutyRates: 'Aucun taux de droits disponible pour ce produit.',
    classifiedHsVersion: 'Classé / Version SH',
    all: 'Tous',
    classified: 'Classés',
    notClassified: 'Non classés',
    hsYear: 'Année SH',
    item: 'Article',
    category: 'Catégorie',
    fromTo: 'Du / Au',
    search: 'Rechercher',
    dutyRatesFailed: 'Échec du chargement des taux de droits',
    detailsFailed: 'Échec du chargement des détails du produit',
    saved: 'Produit enregistré avec succès',
    saveFailed: 'Échec de l’enregistrement du produit',
    profileNotReady: 'Le profil utilisateur n’est pas prêt pour la copie',
    noCopyTargets: 'Aucun produit cible sélectionné pour la copie',
    noCopySources: 'Aucun champ source disponible à copier',
    copied: 'Champs copiés avec succès',
    copyFailed: 'Échec de la copie des champs vers les produits',
    copyStillRunning: 'La copie n’est pas terminée et peut encore s’exécuter sur le serveur. Vérifiez les produits avant de copier à nouveau.',
    copyLeaveConfirm: 'Une copie est en cours. Si vous quittez cette page, elle continue sur le serveur, mais vous ne saurez pas si elle a réussi. Quitter quand même ?',
    createRequiredFields: 'L’ID du produit et la description sont obligatoires',
    created: 'Produit créé avec succès',
    createFailed: 'Échec de la création du produit',
    classifyRequirements: 'Une description douanière ou un code SH existant est requis pour classer ce produit',
    classifying: 'Classification du produit…',
    noHsCode: 'Aucun code SH renvoyé par le classificateur',
    noHsYear: 'La classification a été renvoyée sans information d’année SH',
    classifiedOk: 'Produit classé avec succès',
    classifyCancelled: 'Classification annulée ou échouée',
    classifierNotStarted: 'La fenêtre de classification ne s’est pas chargée. Cliquez pour réessayer.',
    reclassifyStarted: 'Reclassement démarré',
    reclassifyFailed: 'Échec du démarrage du reclassement',
    exportFailed: 'Échec de l’exportation',
    complianceFailed: 'Échec du chargement des données de conformité',
    tariffSaved: 'Code tarifaire enregistré',
    tariffSaveFailed: 'Échec de l’enregistrement du code tarifaire',
    tariffSuggested: 'Code tarifaire suggéré par le classificateur',
    noTariffCode: 'Aucun code tarifaire renvoyé par le classificateur',
    tariffLookupFailed: 'Échec de la recherche du code tarifaire',
    deletingDestination: 'Suppression de la destination {destination}…',
    destinationDeleted: 'Destination supprimée',
    destinationDeleteFailed: 'Échec de la suppression de la destination'
  },
  rules: {
    title: 'Règles de classification',
    subtitle: 'Consultez les règles de classification enregistrées qui remplissent automatiquement les réponses lors de la classification des produits.',
    count: 'Vous avez {count} règles',
    create: 'Créer une règle',
    classifyFirst: 'Classez d’abord un produit pour créer une règle.',
    filterPlaceholder: 'Filtrer par nom ou article…',
    filterAria: 'Filtrer les règles',
    rowHint: 'Cliquez sur une ligne pour afficher les détails de la règle',
    colRuleName: 'Nom de la règle',
    colItem: 'Article',
    colSip: 'SIP',
    detailsTitle: 'Détails de la règle',
    ruleNameLabel: 'Nom de la règle :',
    itemLabel: 'Article :',
    sipLabel: 'SIP :',
    constraints: 'Contraintes',
    constraintsCaption: 'Contraintes de la règle sélectionnée',
    colCategory: 'Catégorie',
    colType: 'Type',
    colValues: 'Valeurs',
    typeSelection: 'Sélection',
    typeValued: 'Valorisé',
    deleting: 'Suppression…',
    deleteRule: 'Supprimer la règle',
    close: 'Fermer',
    createTitle: 'Créer une règle de classification',
    ruleNameField: 'Nom de la règle',
    ruleNamePlaceholder: 'ex. T-shirts en coton',
    itemNameField: 'Nom de l’article',
    itemNamePlaceholder: 'ex. T-shirt',
    sipField: 'SIP',
    sipPlaceholder: 'ex. 6109.10',
    hsCodeLabel: 'Code SH :',
    originLabel: 'Origine :',
    destinationLabel: 'Destination :',
    interactions: 'Interactions',
    selectAll: 'Tout sélectionner',
    unselectAll: 'Tout désélectionner',
    selectedCount: 'Interactions sélectionnées : {count}',
    ruleApplied: 'Règle appliquée',
    ruleMismatch: 'Règle non concordante',
    category: 'Catégorie',
    type: 'Type',
    values: 'Valeurs',
    select: 'Sélectionner',
    deleteTitle: 'Supprimer la règle',
    deleteConfirm: 'Cette action est irréversible. Supprimer {name} ?',
    delete: 'Supprimer',
    classifyBeforeCreate: 'Veuillez classer un produit avant de créer une règle.',
    noProfile: 'Aucun profil sélectionné pour les opérations sur les règles',
    requiredFields: 'Le nom de la règle, l’article et le SIP sont obligatoires',
    selectInteraction: 'Sélectionnez au moins une interaction',
    itemInteractionRequired: 'Cette règle ne peut pas être enregistrée sans sélectionner l’interaction de l’article.',
    selectOneOption: 'Sélectionnez une option pour « {label} »',
    enterValues: 'Saisissez des valeurs pour « {label} »',
    valuesMustTotal: 'Les valeurs de « {label} » doivent totaliser 100 %',
    created: 'Règle créée avec succès',
    createFailed: 'Échec de la création de la règle',
    deleted: 'Règle supprimée avec succès',
    deleteFailed: 'Échec de la suppression de la règle'
  },
  table: {
    noData: 'Aucune donnée disponible',
    downloadErrorFile: 'Télécharger le fichier d’erreurs',
    showing: 'Affichage de {from} à {to} sur {total}',
    perPage: '{size} par page',
    requiresAccess: 'Cette fonctionnalité nécessite un accès supplémentaire'
  },
  header: {
    greeting: 'Bonjour {name}'
  }
}

export default fr
